<template>
  <div class="h-full" style="height: 400px">
    <AgGridVue
      style="width: 100%; height: 100%"
      :theme="standardListTheme"
      :column-defs="computedColumnDefs"
      :default-col-def="defaultColDef"
      :auto-group-column-def="autoGroupColumnDef"
      :group-display-type="groupDisplayType"
      :grid-options="gridOptions"
      :loading="loading"
      :rowHeight="100"
      :row-model-type="'serverSide'"
      @grid-ready="onGridReady"
      :key="tableKey"
    />
  </div>
  <ConfirmationModal
    :is-visible="isOpenDeleteEngagementModal"
    :message="'Are you sure you want to delete this engagement?'"
    @confirm="deleteEngagement"
    @cancel="isOpenDeleteEngagementModal = !isOpenDeleteEngagementModal"
  />

</template>

<script>
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import CC_Router_Link_To_Object from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Router_Link_To_Object.vue';
import CC_Transcript_Popup from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Transcript_Popup.vue';
import ConfirmationModal from "@/components/widgetcomponents/ConfirmationModal.vue";
import CC_Delete from "@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Button_Generic_Function.vue";
import { formatNumber } from '@/utils/format';
import {themeQuartz} from 'ag-grid-enterprise';
import {standardListTheme} from '@/utils/common';
import CC_Router_Link_To_Entity from '../widgetcomponents/AG_Grid_Custom_Cells/CC_Router_Link_To_Entity.vue';
import { format } from 'date-fns';

export default {
  components: {
    ConfirmationModal,
    AgGridVue,
    CC_Router_Link_To_Object,
    CC_Transcript_Popup,
  },
  props: {
    // New entity-based props
    entityId: {
      type: Number,
      required: false,
    },
    includeDescendants: {
      type: Boolean,
      required: false,
      default: true
    },
    // Legacy props
    sqlFilters: {
      type: Object,
      required: false,
    },
    grouperColumns: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      gridApi:null,
      gridOptions: {
        rowModelType: 'serverSide',
        serverSideStoreType: 'partial',  // This might be important
        suppressAggFuncInHeader: true,
        cacheBlockSize: 50,
        maxBlocksInCache: 2,
        maxConcurrentDatasourceRequests: 2,
        blockLoadDebounceMillis: 100,
      },
      loading: true,
      groupDisplayType: 'singleColumn',
      defaultColDef: {
        flex: 1,
        sortable: true,
        filter: true,
        resizable: true,
        wrapText:true,
        autoHeight:true
      },
      autoGroupColumnDef: {
        headerName: 'Business Unit',
        autoHeight:true,
        cellRendererParams: {
          suppressCount: false,
        },
      },
      tableKey : 0,
      isOpenDeleteEngagementModal: false,
      deleteEngagementId: null,
    };
  },
  computed: {
    standardListTheme() {
      return standardListTheme
    },
    themeQuartz() {
      return themeQuartz
    },
    computedColumnDefs() {
      return [
        {
          headerName: 'Date',
          headerTooltip: 'Date',
          cellClass: 'cell-theme-left-aligned',
          width: 140,
          field: 'date',
          cellRenderer: CC_Router_Link_To_Object,
          cellRendererParams: {
            dataType: 'engagement',
          },
          sort: 'desc',
          filter: 'agDateColumnFilter',
        },
        {
          headerName: 'Subject',
          headerTooltip: 'Subject',
          cellClass: 'cell-theme-left-aligned',
          minWidth: 200,
          field: 'subject',
          cellRenderer: CC_Router_Link_To_Object,
          cellRendererParams: {
            dataType: 'engagement',
          },
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Entity Name',
          cellClass: 'cell-theme-left-aligned',
          headerTooltip: 'Entity Name',
          minWidth: 200,
          cellRenderer: CC_Router_Link_To_Entity,
          cellRendererParams: {
            dataType: 'entity',
            idField: 'entity',
            nameField: 'entity_name',
          },
          field: 'entity', // Used for server-side sorting
          valueGetter: (params) => params.data?.entity_name, // Display entity_name,
          filter: false,
        },
        {
          headerName: 'Attendees',
          field:'engagement_participants',
          cellClass: 'cell-theme-center-aligned',
          headerTooltip: 'Attendees',
          valueGetter: (params) => {
            if (params.data?.engagement_participants?.length > 0) {
              return params.data.engagement_participants.length;
            }
            return null;
          },
          filter: false,
          sortable: false,
        },
        {
          headerName: 'Channel',
          cellClass: 'cell-theme-left-aligned',
          headerTooltip: 'Channel',
          width: 135,
          field: 'engagement_channel_id', // Used for server-side sorting
          valueGetter: (params) => params.data?.engagement_channel_name, // Display engagement_channel_name
          filter: 'agSetColumnFilter',
          filterParams: {
            values: (params) => {
              axios.get('/api/engagementChannels/')
                .then(response => {
                  params.success(response.data);
                })
                .catch(() => {
                  params.fail();
                });
            },
            valueFormatter: (params) => {
              return params.value.name;
            },
            keyCreator: (params) => {
              return params.value.id;
            },
            debounceMs: 500,
          },
        },
        {
          headerName: 'Type',
          cellClass: 'cell-theme-left-aligned',
          headerTooltip: 'Type',
          width: 115,
          field: 'engagement_type_id', // Used for server-side sorting
          valueGetter: (params) => params.data?.engagement_type_name, // Display engagement_type_name
          filter: 'agSetColumnFilter',
          filterParams: {
            values: (params) => {
              axios.get('/api/engagementtypes/')
                .then(response => {
                  params.success(response.data);
                })
                .catch(() => {
                  params.fail();
                });
            },
            valueFormatter: (params) => {
              return params.value.name;
            },
            keyCreator: (params) => {
              return params.value.id;
            },
            debounceMs: 500,
          },
        },
        {
          headerName: 'Status',
          cellClass: 'cell-theme-left-aligned',
          headerTooltip: 'Status',
          width: 125,
          field: 'status',
          valueFormatter: (params) => {
            switch (params.value) {
              case 'SCHEDULED':
                return 'Scheduled';
              case 'COMPLETED':
                return 'Completed';
              case 'CANCELLED':
                return 'Cancelled';
              case 'RESCHEDULED':
                return 'Rescheduled';
              case 'NO_SHOW':
                return 'No Show';
              default:
                return params.value;
            }
          },
          filterParams: {
            values: ['SCHEDULED', 'COMPLETED', 'CANCELLED', 'RESCHEDULED', 'NO_SHOW'],
            valueFormatter: (params) => {
              switch (params.value) {
                case 'SCHEDULED':
                  return 'Scheduled';
                case 'COMPLETED':
                  return 'Completed';
                case 'CANCELLED':
                  return 'Cancelled';
                case 'RESCHEDULED':
                  return 'Rescheduled';
                case 'NO_SHOW':
                  return 'No Show';
                default:
                  return params.value;
              }
            },
          }
        },
        {
          headerName: 'Engagement Score',
          headerTooltip: 'Engagement Score',
          cellClass: 'cell-theme-left-aligned',
          field: 'engagement_score',
          width: 160,
          aggFunc: 'sum',
          wrapHeaderText: true,
          sortable: false,
          filter: false,
          valueGetter: (params) => {
            // for each participant in engagement_participants, get and avg the engagement score
            if (params.data?.engagement_participants?.length > 0) {
              const scores = params.data.engagement_participants.map(participant => participant.engagement_score);
              const totalScore = scores.reduce((acc, score) => acc + score, 0);
              return totalScore / scores.length;
            }
          },
          valueFormatter: (params) => {
            return formatNumber(params.value);
          },
        },
        {
          headerName: 'Transcript',
          headerTooltip: 'Transcript',
          cellClass: 'flex items-center justify-center',
          field: 'transcription_summary',
          cellRenderer: CC_Transcript_Popup,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: ['Has Value', 'No Value'],
            valueFormatter: (params) => {
              return params.value === 'Has Value' ? 'Yes' : 'No';
            },
          },
        },
        {
          headerName: '',
          cellClass: 'flex items-center justify-center',
          maxWidth: 75,
          sortable: false,
          filter: false,
          cellRenderer: CC_Delete,
          cellRendererParams: {
            function_type:'delete_v2',
            openDeleteConfirmationModal: data => {
              this.isOpenDeleteEngagementModal = true;
              this.deleteEngagementId = data.id;
            },
          },
        },
      ];
    },
  },
  methods: {
    getRowId(params){
      return params.data.id;
    },
    async onGridReady(params) {
      this.gridApi = params.api;

      const datasource = {
        getRows: async (paramsRequest) => {
          this.loading = true;
          this.gridApi.setGridOption('loading', true);

          try {

            // Format date fields in the filter model
            const filterModel = { ...paramsRequest.request.filterModel };
            Object.keys(filterModel).forEach((key) => {
              const filter = filterModel[key];
              if (filter.filterType === 'date') {
                if (filter.dateFrom) {
                  const date = new Date(filter.dateFrom);
                  filter.dateFrom = format(date, 'yyyy-MM-dd');
                }
                if (filter.dateTo) {
                  const date = new Date(filter.dateTo);
                  filter.dateTo = format(date, 'yyyy-MM-dd');
                }
              }
            });
            
            // Build request payload
            const payload = {
              start_row: paramsRequest.request.startRow,
              end_row: paramsRequest.request.endRow,
              sort_model: paramsRequest.request.sortModel || [],
              filter_model: filterModel || {},
            };

            // Add entity filtering
            if (this.entityId) {
              payload.entity_id = this.entityId;
              payload.include_descendants = this.includeDescendants;
            } else if (this.sqlFilters) {
              // Handle legacy sqlFilters format
              Object.assign(payload, this.sqlFilters);
            }

            const response = await axios.post('/api/engagements/list/', payload);

            if (response.data?.rowData) {
              paramsRequest.success({
                rowData: response.data.rowData,
                rowCount: response.data.rowData.length < 50 ? paramsRequest.request.startRow + response.data.rowData.length : undefined,
              });
            } else {
              paramsRequest.success(
                {
                  rowData: [],
                  rowCount:0
                }
              );
            }
          } catch (error) {
            console.error('Error fetching engagements:', error);
            paramsRequest.fail();
          } finally {
            this.loading = false;
            this.gridApi.setGridOption('loading', false);
          }
        },
      };

      this.gridApi.setGridOption('serverSideDatasource', datasource);
      params.api.sizeColumnsToFit();
      window.addEventListener('resize', this.onWindowResize);
    },

    refreshData() {
      if (this.gridApi) {
        this.gridApi.refreshServerSide({ purge: true });
      }
    },

    onWindowResize() {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    },

    async deleteEngagement() {
      try {
        await axios.delete(`api/engagements/${this.deleteEngagementId}/`);

        this.isOpenDeleteEngagementModal = false;
        this.deleteEngagementId = null;
        this.refreshData();
      } catch (error) {
        console.error('Error deleting engagement', error);
      }
    },
  },

  watch: {
    entityId() {
      this.refreshData();
    },
    sqlFilters: {
      deep: true,
      handler() {
        this.refreshData();
      }
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  }
};
</script>
